import { IAuthenticationManager } from '../../controller/interface/auth';
import { IMediaAPI } from '../../api/interface/media';
import { IProgressPromise, IProgressPromiseGenerator } from '../../interface/promise';
import { IMediumDto } from '../../model';

interface IMediaResponseType {
    blob: Blob;
    none: void;
}

const DEFAULT_MEDIUM_SIZE = 1024 * 1024 * 5; /* 5MiB */

function _isMediumDto(medium: Object): medium is IMediumDto {
    return 'id' in medium;
}

export function download<K extends keyof IMediaResponseType, V extends IMediaResponseType[K]>(
    promiser: IProgressPromiseGenerator,
    mediaAPI: IMediaAPI,
    type: K,
    media: Array<IMediumDto | string>,
    auth: IAuthenticationManager,
): IProgressPromise<Array<V>> {
    return promiser.promise(async function (resolve, reject, progressCb) {
        try {
            const accessToken = await auth.renew();
            let fetchError: string | null = null;

            progressCb(0.01);

            const totalBytesToDownload = media.reduce(
                (total: number, medium: IMediumDto | string) =>
                    total + (_isMediumDto(medium) ? medium.mediumSize : DEFAULT_MEDIUM_SIZE),
                0,
            );

            const responses: Array<V> = [];

            let progressUptoThisFetch = 0; /* as we loop through keep a running counter of all weightings up to this point */

            /* download each fetch in order sequentially */
            for (let idx = 0; idx < media.length; idx++) {
                const medium = media[idx];

                const mediaId = _isMediumDto(medium) ? medium.id : medium;
                const mediumSize = _isMediumDto(medium) ? medium.mediumSize : DEFAULT_MEDIUM_SIZE;

                /* how much is this request worth overall to the progress */
                const progressWeighting = mediumSize / totalBytesToDownload;

                let fetcher: IProgressPromise<V> | null = null;

                switch (type) {
                    case 'blob':
                        fetcher = mediaAPI.getMedia(accessToken, mediaId) as IProgressPromise<V>;
                        break;

                    case 'none':
                        fetcher = mediaAPI.getMediaNoResponse(
                            accessToken,
                            mediaId,
                        ) as IProgressPromise<V>;
                        break;
                }

                /* Fix warning: https://eslint.org/docs/rules/no-loop-func */
                const progressCounterSoFar = progressUptoThisFetch;

                fetcher?.onProgress(progressNow => {
                    const progressVal = Math.max(
                        Math.min(progressCounterSoFar + progressNow * progressWeighting, 1.0),
                        0.0,
                    );
                    progressCb(progressVal);
                });

                let result: V | null = null;

                try {
                    result = await fetcher;
                } catch (error) {
                    console.error(`Download all media failed: ${JSON.stringify(error)}`);
                    fetchError = `${error}`;
                    break;
                }

                if (type === 'blob') {
                    responses.push(result ?? (null as any));
                }

                progressUptoThisFetch += progressWeighting;

                progressCb(progressUptoThisFetch);
            }

            fetchError ? reject(fetchError) : resolve();
        } catch (error) {
            reject(error);
        }
    });
}

export default download;
